import React, { useEffect, useState } from 'react';
import {
  getHiddenComments,
  unHiddenCommentApi,
} from '../../../../api/admin/comment.api';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import { ChevronLeft, ChevronRight, Eye, EyeOff, MessageSquare, ThumbsDown, ThumbsUp } from 'lucide-react';
import { AdminButton, AdminCard, AdminCardFooter, AdminPageHeader, AdminStatusPill } from '../../ui';
import '../report-moderation/report-moderation.css';

interface HiddenComment {
  id: string;
  postId: string | null;
  userId: string;
  userFullName: string;
  avatar: string;
  content: string;
  upvotesCount: number;
  downvotesCount: number;
  status: number;
  replies: any;
  totalReply: number;
  updatedAt: string;
  createdAt: string;
  currentUserHasLike: boolean;
  currentUserHasUnlike: boolean;
  commentType: number;
  username: string;
}

const ManagementHiddenComment = () => {
  const [comments, setComments] = useState<HiddenComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const fetchHiddenComments = async () => {
      try {
        setLoading(true);
        const response = await getHiddenComments({ pageNumber, pageSize });
        setComments(response.data || []);
        setTotalItems(response.total || 0);
      } catch (error) {
        console.error('Error fetching hidden comments:', error);
        toast.error('Không thể tải danh sách bình luận');
      } finally {
        setLoading(false);
      }
    };

    fetchHiddenComments();
  }, [pageNumber, pageSize]);

  const handleUnhideComment = async (comment: HiddenComment) => {
    if (processingIds.has(comment.id)) return;

    try {
      setProcessingIds((prev) => new Set(prev).add(comment.id));
      await unHiddenCommentApi(
        comment.id,
        null, // lastReportId - since we're unhiding, we don't need this
        null, // lastReportDate - since we're unhiding, we don't need this,
        0,
        comment.commentType.toString()
      );

      setComments((prev) => prev.filter((c) => c.id !== comment.id));
      setTotalItems((prev) => prev - 1);
      toast.success('Đã hiển thị lại bình luận');
    } catch (error) {
      console.error('Error unhiding comment:', error);
      toast.error('Không thể hiển thị lại bình luận');
    } finally {
      setProcessingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(comment.id);
        return newSet;
      });
    }
  };

  const totalPages = Math.ceil(totalItems / pageSize);

  return (
    <div className="adm-page">
      <AdminPageHeader
        title="Quản lý bình luận bị ẩn"
        description="Danh sách bình luận đang bị ẩn. Bạn có thể hiển thị lại từng bình luận."
      />

      <AdminCard flush>
        <div className="adm-table-wrap">
          <table className="adm-table" style={{ minWidth: 980 }}>
            <thead>
              <tr>
                <th>Người dùng</th>
                <th>Nội dung</th>
                <th>Tương tác</th>
                <th>Trạng thái</th>
                <th>Thời gian</th>
                <th style={{ textAlign: 'right' }}>Hành động</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', color: 'var(--adm-muted)' }}>
                    Đang tải dữ liệu...
                  </td>
                </tr>
              ) : comments.length > 0 ? (
                comments.map((comment) => (
                  <tr key={comment.id}>
                    <td>
                      <div className="adm-hidden-user">
                        {comment.avatar && <img src={comment.avatar} alt={comment.userFullName} />}
                        <div>
                          <div style={{ fontWeight: 600, color: 'var(--adm-heading)', whiteSpace: 'nowrap' }}>{comment.userFullName}</div>
                          <div className="adm-sub">@{comment.username}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="adm-clamp-2" style={{ maxWidth: 320, whiteSpace: 'normal' }}>{comment.content}</div>
                    </td>
                    <td>
                      <span className="adm-react">
                        <span>
                          <ThumbsUp size={16} aria-label="Thích" />
                          {comment.upvotesCount}
                        </span>
                        <span>
                          <ThumbsDown size={16} aria-label="Không thích" />
                          {comment.downvotesCount}
                        </span>
                        <span>
                          <MessageSquare size={16} aria-label="Trả lời" />
                          {comment.totalReply}
                        </span>
                      </span>
                    </td>
                    <td>
                      {comment.status === 1 ? (
                        <AdminStatusPill tone="danger" icon={<EyeOff size={12} aria-hidden="true" />}>
                          Đã ẩn
                        </AdminStatusPill>
                      ) : (
                        <AdminStatusPill tone="success" icon={<Eye size={12} aria-hidden="true" />}>
                          Hiển thị
                        </AdminStatusPill>
                      )}
                    </td>
                    <td className="adm-sub" style={{ whiteSpace: 'nowrap' }}>
                      <div>Tạo: {dayjs(comment.createdAt).format('DD/MM/YYYY HH:mm')}</div>
                      <div>Sửa: {dayjs(comment.updatedAt).format('DD/MM/YYYY HH:mm')}</div>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <AdminButton
                        size={32}
                        variant="soft"
                        icon={<Eye size={14} />}
                        onClick={() => handleUnhideComment(comment)}
                        disabled={processingIds.has(comment.id)}
                      >
                        {processingIds.has(comment.id) ? 'Đang xử lý...' : 'Hiển thị lại'}
                      </AdminButton>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', color: 'var(--adm-muted)' }}>
                    Không có bình luận bị ẩn
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {comments.length > 0 && (
          <AdminCardFooter style={{ justifyContent: 'space-between', borderTop: '1px solid var(--adm-line)', padding: '14px 20px' }}>
            <span style={{ fontSize: 13, color: 'var(--adm-muted)' }}>
              Trang {pageNumber}/{totalPages}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <AdminButton
                size={32}
                variant="fill"
                icon={<ChevronLeft size={14} />}
                onClick={() => setPageNumber((prev) => Math.max(1, prev - 1))}
                disabled={pageNumber === 1}
              >
                Trước
              </AdminButton>
              <AdminButton
                size={32}
                variant="soft"
                iconRight={<ChevronRight size={14} />}
                onClick={() => setPageNumber((prev) => prev + 1)}
                disabled={pageNumber >= totalPages}
              >
                Sau
              </AdminButton>
            </div>
          </AdminCardFooter>
        )}
      </AdminCard>
    </div>
  );
};

export default ManagementHiddenComment;
